import { Link, useNavigate } from "react-router-dom";
import { Heart, Share2, Repeat, Headphones, Wand2, ListPlus, ListCheck } from "lucide-react";
import WaveformPlayer from "@/components/WaveformPlayer";
import { API } from "@/lib/api";
import { toggleLike, shareTrack } from "@/lib/showcase";
import { useAuth } from "@/context/AuthContext";
import { abEvent } from "@/lib/ab";
import { usePlaylist } from "@/lib/playlist";

const base = API.replace(/\/api$/, "");

export const likeUrlFor = (t) => `/dashboard?prompt=${encodeURIComponent(t.prompt || t.title)}&genre=${encodeURIComponent(t.genre || "")}&mood=${encodeURIComponent(t.mood || "")}`;

export function LikeButton({ t, onChange, size = "sm" }) {
  const act = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try { const d = await toggleLike(t.id); onChange?.({ ...t, ...d }); } catch { /* ignore */ }
  };
  const big = size === "lg";
  return (
    <button type="button" onClick={act} aria-pressed={t.liked} title={t.liked ? "Unlike" : "Like this track"}
      className={`inline-flex items-center gap-1.5 rounded-full border transition-colors ${big ? "h-10 px-4 text-sm" : "h-7 px-2.5 text-xs"} ${t.liked ? "border-rose-500/60 bg-rose-500/10 text-rose-400" : "border-border text-muted-foreground hover:text-rose-400 hover:border-rose-500/40"}`}
      data-testid={`like-${t.id}`}>
      <Heart size={big ? 16 : 12} className={t.liked ? "fill-current" : ""} /> <span data-testid={`like-count-${t.id}`}>{t.likes}</span>
    </button>
  );
}

export function PlaylistButton({ t, size = "sm" }) {
  const pl = usePlaylist();
  const on = pl.has(t.id);
  const big = size === "lg";
  return (
    <button type="button" onClick={(e) => { e.preventDefault(); pl.toggle(t.id); }} title={on ? "Remove from my playlist" : "Add to my playlist"} aria-pressed={on}
      className={`inline-flex items-center gap-1.5 rounded-full border transition-colors ${on ? "border-fuchsia-400/60 text-fuchsia-300 bg-fuchsia-500/10" : "border-border text-muted-foreground hover:text-foreground"} ${big ? "h-10 px-4 text-sm" : "h-7 px-2.5 text-xs"}`}
      data-testid={`playlist-btn-${t.id}`}>
      {on ? <ListCheck size={big ? 16 : 12} /> : <ListPlus size={big ? 16 : 12} />} {on ? "In playlist" : "Playlist"}
    </button>
  );
}

export function ShareButton({ t, size = "sm" }) {
  const big = size === "lg";
  return (
    <button type="button" onClick={(e) => { e.preventDefault(); shareTrack(t); }} title="Share — rich preview on social networks"
      className={`inline-flex items-center gap-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground transition-colors ${big ? "h-10 px-4 text-sm" : "h-7 px-2.5 text-xs"}`} data-testid={`share-${t.id}`}>
      <Share2 size={big ? 16 : 12} /> Share
    </button>
  );
}

export function RemixButton({ t, className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => { abEvent("cta"); try { sessionStorage.setItem("mf_remix", JSON.stringify({ prompt: t.prompt || t.title, genre: t.genre, mood: t.mood })); } catch { /* noop */ } navigate(user ? likeUrlFor(t) : "/login"); };
  return (
    <button type="button" onClick={go} className={`inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors ${className}`} data-testid={`remix-${t.id}`}>
      <Wand2 size={14} /> Create a sound like this
    </button>
  );
}

const BADGE = { weekly_top_1: ["#1 this week", "bg-rose-500 text-white"], weekly_top_2: ["#2 this week", "bg-rose-500/80 text-white"], weekly_top_3: ["#3 this week", "bg-rose-500/60 text-white"], viral: ["Viral · 50+ shares", "bg-fuchsia-500 text-white glow-primary"], popular: ["Popular", "bg-amber-500/20 text-amber-300 border border-amber-500/40"], staff_pick: ["Staff pick", "bg-primary/15 text-primary border border-primary/40"] };

export function Badges({ t, className = "" }) {
  const list = (t.badges || []).filter((b) => BADGE[b]);
  if (!list.length) return null;
  return <div className={`flex flex-wrap gap-1 ${className}`} data-testid={`badges-${t.id}`}>{list.map((b) => <span key={b} className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${BADGE[b][1]}`} data-testid={`badge-${b}-${t.id}`}>{BADGE[b][0]}</span>)}</div>;
}

export default function TrackCard({ t, i, onChange, rank }) {
  return (
    <article className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors relative" style={{ animationDelay: `${i * 40}ms` }} data-testid={`showcase-track-${t.id}`}>
      {rank && <span className={`absolute -top-2.5 left-5 text-[10px] font-mono px-2 py-0.5 rounded text-white ${t.week_shares != null ? "bg-fuchsia-500" : "bg-rose-500"}`} data-testid={`rank-${t.id}`}>#{rank} THIS WEEK · {t.week_shares != null ? `${t.week_shares} shares` : `${t.week_likes} ♥`}</span>}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={t.page_url} className="font-display font-bold text-base md:text-lg truncate block hover:text-primary transition-colors" data-testid={`showcase-title-${t.id}`}>{t.title}</Link>
          <p className="text-xs text-muted-foreground mt-0.5">by {t.artist_url ? <Link to={t.artist_url} className="hover:text-primary hover:underline" data-testid={`artist-link-${t.id}`}>{t.artist}</Link> : t.artist}</p>
        </div>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-primary/15 text-primary shrink-0 uppercase">{t.genre}</span>
      </div>
      {!rank && <Badges t={t} />}
      <p className="text-sm text-muted-foreground line-clamp-2">{t.prompt}</p>
      <WaveformPlayer src={`${base}${t.audio_url}`} label={t.mood ? `${t.mood} · ${t.duration || 30}s` : "Full mix"} testId={`showcase-player-${t.id}`} />
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-mono text-muted-foreground">
        {t.loop && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border"><Repeat size={10} /> loop</span>}
        {t.bpm && <span className="px-2 py-0.5 rounded-full border border-border">{t.bpm} bpm</span>}
        {t.key && <span className="px-2 py-0.5 rounded-full border border-border">{t.key}</span>}
        <span className="ml-auto inline-flex items-center gap-1"><Headphones size={11} /> {t.plays}</span>
        {t.shares > 0 && <span className="inline-flex items-center gap-1" title="Share-button clicks" data-testid={`shares-${t.id}`}><Share2 size={11} /> {t.shares}</span>}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <LikeButton t={t} onChange={onChange} /><ShareButton t={t} /><PlaylistButton t={t} />
        <Link to={t.page_url} className="ml-auto text-xs text-primary hover:underline" data-testid={`open-${t.id}`}>Open →</Link>
      </div>
    </article>
  );
}
